import React from "react";
import { StyleSheet, View } from "react-native";
import { SpinnerGapIcon, WarningCircleIcon } from "@phosphor-icons/react";

import CustomText from "../../../common/general/CustomText/CustomText";
import { RubricParseStatus } from "../../../../graphql/generated/graphql";

interface RubricProcessingViewModel {
  parseStatus: RubricParseStatus;
  parseError?: string | null;
}

interface RubricProcessingProps {
  rubric: RubricProcessingViewModel;
}

const parseStatusLabels: Record<RubricParseStatus, string> = {
  [RubricParseStatus.Failed]: "Parsing failed",
  [RubricParseStatus.Parsing]: "Parsing rubric",
  [RubricParseStatus.Pending]: "Queued for parsing",
  [RubricParseStatus.Ready]: "Ready",
};

function RubricProcessing({ rubric }: RubricProcessingProps) {
  const isFailed = rubric.parseStatus === RubricParseStatus.Failed;

  return (
    <View style={isFailed ? styles.failedPanel : styles.processingPanel}>
      {isFailed ? (
        <WarningCircleIcon color="#B42318" size={28} weight="bold" />
      ) : (
        <SpinnerGapIcon color="#3E68FE" size={28} weight="bold" />
      )}
      <CustomText
        text={parseStatusLabels[rubric.parseStatus]}
        size="m"
        weight="bold"
        style={isFailed ? styles.errorText : styles.primaryText}
        useTranslationText={false}
      />
      <CustomText
        text={
          isFailed
            ? rubric.parseError || "The rubric could not be parsed. Select another file and try again."
            : "Analyzing the document, identifying evaluation categories, and building the scoring model."
        }
        size="s"
        style={isFailed ? styles.errorText : styles.secondaryText}
        useTranslationText={false}
      />
    </View>
  );
}

const styles = StyleSheet.create({
  primaryText: {
    color: "#101828",
  },
  secondaryText: {
    color: "#344054",
  },
  errorText: {
    color: "#B42318",
  },
  processingPanel: {
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    minHeight: 260,
    paddingHorizontal: 20,
    paddingVertical: 28,
    borderRadius: 8,
    backgroundColor: "#FFFFFF",
  },
  failedPanel: {
    alignItems: "center",
    justifyContent: "center",
    gap: 10,
    minHeight: 180,
    padding: 20,
    borderRadius: 8,
    backgroundColor: "#FEF3F2",
    borderWidth: 1,
    borderColor: "#FECDCA",
    marginBottom: 16,
  },
});

export default RubricProcessing;
